import Vue from "vue";
import Router from "vue-router";
import moment from "moment";
import jwt_decode from "jwt-decode";
import store from "../store/index";

// Importaciones de vistas
import Home from "@/views/Home.vue";
import Login from "@/views/Auth/Login.vue";
import AuthView from "@/views/Auth/AuthView.vue";
import ErrorPage from "@/views/ErrorPage.vue";
import RouterProject from "@/views/Projects/RouterProject.vue";
import Projects from "@/views/Projects/Projects.vue";
import AccessDenied from "@/views/AccessDenied.vue"; // Nueva vista
import NoAcces from "@/views/NoAcces.vue";
import userPerfil from "@/views/Maintenance/UserPerfil.vue";
// Importaciones de módulos de rutas
import shop from "./routes/shop";
import accounting from "./routes/accounting";
import bank from "./routes/bank";
import entity from "./routes/entity";
import invoices from "./routes/invoice";
import cxc from "./routes/cxc";
import cxp from "./routes/cxp";
import inventory from "./routes/invetory";
import maintenance from "./routes/maintenance";
import restaurant from "./routes/restaurant";


Vue.use(Router);

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: {
      requiresAuth: true, // ← Debe estar dentro de meta

    }
  },
  {
    path: "/userPerfil",
    name: "UserPerfil",
    component: userPerfil,
    meta: {
      requiresAuth: true, // ← Debe estar dentro de meta

    }
  },
  {
    path: "/auth",
    name: "AuthView",
    component: AuthView,
    meta: {
      libre: true
    },
    children: [
      {
        path: "login",
        name: "Login",
        component: Login,
        meta: {
          libre: true
        }
      }
    ]
  },
  {
    path: '/access-denied',
    name: "AccessDenied",
    component: AccessDenied,
    meta: {
      libre: true
    }
  },
  {
    path: '/no-acces',
    name: "NoAcces",
    component: NoAcces,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/error',
    name: "ErrorPage",
    component: ErrorPage,
    meta: {
      libre: true
    }
  },
  {
    path: '*',
    redirect: '/error'
  },
  bank,
  maintenance,
  shop,
  entity,
  cxc,
  cxp,
  invoices,
  accounting,
  inventory,
  restaurant
];

const router = new Router({
  base: process.env.BASE_URL,
  routes
});

router.beforeEach(async (to, from, next) => {
  const publicRoutes = ['Login', 'ErrorPage', 'AuthView', 'AccessDenied'];
  const excludedPaths = ['/viewDocument', '/ViewInvoice']; // Corregido typo

  // 1. Verificación de rutas públicas mejorada
  const isPublicRoute = () => {
    if (to.meta?.libre) return true;
    if (publicRoutes.includes(to.name)) return true;
    return excludedPaths.some(path => to.path.startsWith(path));
  };

  // Permitir acceso inmediato a rutas públicas
  if (isPublicRoute()) {
    return next();
  }

  // 2. Sistema de autenticación reforzado
  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;

      const { exp } = jwt_decode(token);
      const isValid = moment().unix() < exp;
      
      // Actualizar estado en Vuex 
      store.commit('auth/SET_AUTH_STATUS', isValid);
      return isValid;
    } catch (error) {
      console.error('Error verificando token:', error);
      return false;
    }
  };

  try {
    // 3. Flujo de autenticación estricto
    let isAuthenticated = checkAuthStatus();

    // Intento de autologin solo si no hay autenticación
    if (!isAuthenticated) {
      await store.dispatch("auth/autoLogin");
      isAuthenticated = checkAuthStatus(); // Re-verificar después del autoLogin
    }

    // Redirección forzosa si falla la autenticación
    if (!isAuthenticated) {
      await store.dispatch("auth/logOut");
      return next({
        name: 'Login',
        query: {
          redirect: to.fullPath,
          reason: 'session_expired'
        }
      });
    }

    // 4. Gestión de permisos optimizada
    
    if (!store.state.auth.globalFeatures.length) {
      await store.dispatch("auth/GET_FEATURES");
      
      // Validar permisos obtenidos
      if (!store.state.auth.globalFeatures?.length) {
        throw new Error('Permisos no configurados');
      }
    }

    // 5. Sistema de autorización dinámico
    const requiredPermissions = [].concat(
      to.meta?.code || [], 
      to.matched.flatMap(record => record.meta.code || [])
    ).filter(Boolean);

    if (requiredPermissions.length > 0) {
      const hasPermission = requiredPermissions.some(code => 
        store.state.auth.globalFeatures.includes(code)
      );

      if (!hasPermission) {
        return next({
          name: 'AccessDenied',
          query: {
            missingPermissions: requiredPermissions.join(','),
            attemptedRoute: to.fullPath
          }
        });
      }
    }

    // 5b. Restricción por usuario específico (rutas Beta)
    const onlyUserId = to.meta?.onlyUserId ||
      to.matched.map(record => record.meta?.onlyUserId).find(Boolean);
    if (onlyUserId && store.state.auth.user?.user?.user_id !== onlyUserId) {
      return next({
        name: 'AccessDenied',
        query: { attemptedRoute: to.fullPath }
      });
    }

    // 6. Validación adicional de token en cada navegación
    const token = localStorage.getItem("token");
    if (token) {
      const { exp } = jwt_decode(token);
      const tokenExpirationThreshold = 300; // 5 minutos en segundos
      
      if (exp - moment().unix() < tokenExpirationThreshold) {
      
        await store.dispatch("auth/refreshToken");
      }
    }

    next();
  } catch (error) {
    console.error('Error en guardián de navegación:', error);

    // Manejo de errores específicos (error.message puede venir undefined)
    const message = (error && error.message) || '';
    if (message.includes('token')) {
      await store.dispatch("auth/logOut");
      next({ name: 'Login' });
    } else if (message.includes('Permisos')) {
      next({
        name: 'AccessDenied',
        query: {
          error: error.message,
          attemptedRoute: to.fullPath
        }
      });
    } else {
      next({ 
        name: 'ErrorPage',
        query: {
          errorCode: 'ROUTER_FAILURE',
          message: error.message
        }
      });
    }
  }
});
export default router;